import { SERVICE_TYPES } from '@bagonboard/wiseman';
import InputAdornment from '@material-ui/core/InputAdornment';
import CalendarIcon from '@material-ui/icons/CalendarToday';
import FlightIcon from '@material-ui/icons/FlightTakeoff';
import SearchIcon from '@material-ui/icons/Search';
import {
  connect as formikConnect, FastField, Field, getIn,
} from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import Datetime from 'react-datetime';
import { connect } from 'react-redux';
import { compose } from 'redux';

import {
  CustomInput, CustomSelect,
} from '../../../../../components';
import GridContainer from '../../../../../components/Grid/GridContainer';
import GridItem from '../../../../../components/Grid/GridItem';
import * as actions from '../../../../../reducers/FlightProducts/actions';
import { OrderCard } from '../../../components';
import BagSelector from '../BagSelector';
import AddressForm from './Address';
import CompleteOrder from './CompleteOrder';
import FlightTable from './FlightTable';
import PickupDeliverySelector from './PickupDeliverySelector';
import {
  DatetimeWrapper, SelectorTitle, ServiceDetails, ServiceDetailsTitle, ServiceSelector,
} from './style';
import SubmitButton from './SubmitButton';

const {
  CHECKIN,
  TRANSFER,
  CITY_DELIVERY,
  SKIP_BAGGAGE_CLAIM,
} = SERVICE_TYPES;

const yesterday = Datetime.moment().subtract(1, 'day');
const isSelectableDay = date => Datetime.moment(date).isAfter(yesterday);
const getFormError = (form, prop) => getIn(form.touched, prop) && getIn(form.errors, prop);

const mapServicesType = {
  [CHECKIN]           : 'Pick-up and Checkin',
  [TRANSFER]          : 'Airport Delivery',
  [CITY_DELIVERY]     : 'City Delivery',
  [SKIP_BAGGAGE_CLAIM]: 'Skip Baggage Claim',
};
const getServiceValues = products => products.map(({ service }) => (
  {
    value: service,
    text : mapServicesType[service],
  }
));

const FlightSearch = ({ flightProductsFetch, flightProducts, formik }) => {
  const flightSelected = getIn(formik, 'values.flightSelected');
  const serviceType = getIn(formik, 'values.service.type');
  const products = getIn(flightProducts, `data[${flightSelected}].products`) || [];

  const searchFlight = (data) => {
    formik.setValues({
      ...formik.values,
      service: {
        type   : '',
        numBags: 1,
        address: {},
        pickup : {
          date: '',
          time: {},
        },
        delivery: {
          date: '',
          time: {},
        },
      },
    });
    flightProductsFetch(data);
  };

  return (
    <OrderCard
      title="Search flight"
      icon={<SearchIcon />}
    >
      <GridContainer spacing={4}>
        <GridItem xs={12} sm={12} md={5}>
          <FastField name="flight.number">
            {({ field, form }) => {
              const error = getFormError(form, field.name);
              return (
                <CustomInput
                  data-testid="input.flight-number"
                  error={!!error}
                  helpText={error}
                  labelText="Flight number"
                  id="flightNumber"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    endAdornment: (<InputAdornment position="end"><FlightIcon /></InputAdornment>),
                    ...field,
                  }}
                />
              );
            }}
          </FastField>
        </GridItem>
        <GridItem xs={12} sm={12} md={5}>
          <FastField name="flight.date">
            {({ field, form }) => {
              const error = getFormError(form, field.name);
              return (
                <DatetimeWrapper
                  dateFormat="DD/MM/YYYY"
                  timeFormat={false}
                  isValidDate={isSelectableDay}
                  {...field}
                  onChange={date => form.setFieldValue(field.name, date.format('DD/MM/YYYY'))}
                  renderInput={(dateInputProps, open, close) => (
                    <CustomInput
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        'data-testid': 'input.flight-date',
                        ...dateInputProps,
                        endAdornment : (<InputAdornment position="end"><CalendarIcon /></InputAdornment>),
                        disabled     : true,
                      }}
                      onBlur={close}
                      onFocus={open}
                      labelText="Flight date"
                      error={!!error}
                      helpText={error}
                    />
                  )}
                />
              );
            }}
          </FastField>
        </GridItem>
        <GridItem xs={12} sm={12} md={2}>
          <SubmitButton onClick={searchFlight} isFetching={flightProducts.loading} />
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <FlightTable flightProducts={flightProducts} />
        </GridItem>
      </GridContainer>
      {flightSelected && (
        <ServiceSelector>
          <GridContainer direction="column">
            <GridItem xs={12} sm={12} md={12}>
              <SelectorTitle>Service details</SelectorTitle>
            </GridItem>
            <GridItem xs={12} sm={12} md={6}>
              <Field name="service.type">
                {({ field, form }) => {
                  const emptyProductsError = !products.length && 'No services available for this flight';
                  const error = emptyProductsError || getFormError(form, field.name);
                  const onChange = (e) => {
                    form.setValues({
                      ...form.values,
                      service: {
                        ...form.values.service,
                        pickup: {
                          date: '',
                          time: {},
                        },
                        delivery: {
                          date: '',
                          time: {},
                        },
                      },
                    });
                    field.onChange(e);
                  };
                  return (
                    <CustomSelect
                      error={!!error}
                      helpText={error}
                      id="service"
                      labelText="Type of service"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        ...field,
                        onChange,
                        disabled: !products.length,
                      }}
                      data-testid="select.type-service"
                      options={getServiceValues(products)}
                    />
                  );
                }}
              </Field>
            </GridItem>
          </GridContainer>
          { serviceType && products.length > 0 && (
            <ServiceDetails>
              <GridContainer direction="column">
                <GridItem xs={12} sm={12}>
                  <ServiceDetailsTitle>
                    Service details
                  </ServiceDetailsTitle>
                </GridItem>
                <GridItem xs={12} sm={12}>
                  <FastField name="service.numBags" component={BagSelector} />
                </GridItem>
                <GridItem xs={12} sm={12}>
                  <Field name="service.address" component={AddressForm} />
                </GridItem>
                <GridItem xs={12}>
                  <PickupDeliverySelector
                    flightSelected={flightSelected}
                    serviceType={serviceType}
                  />
                </GridItem>
              </GridContainer>
            </ServiceDetails>
          )}
        </ServiceSelector>
      )}
      <CompleteOrder />
    </OrderCard>
  );
};

FlightSearch.propTypes = {
  flightProductsFetch: PropTypes.func.isRequired,
  flightProducts     : PropTypes.shape({
    loading: PropTypes.bool,
    data   : PropTypes.array,
    error  : PropTypes.string,
  }),
  formik: PropTypes.shape({
    setValues: PropTypes.func.isRequired,
    values   : PropTypes.object.isRequired,
  }).isRequired,
};

FlightSearch.defaultProps = {
  flightProducts: {},
};

const mapStateToProps = ({ flightProducts }) => ({
  flightProducts,
});

const mapDispatchToProps = dispatch => ({
  flightProductsFetch: data => dispatch(actions.flightProductsFetch(data)),
});

export default compose(
  formikConnect,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(FlightSearch);
